'use client'

import React, { useRef, useCallback, KeyboardEvent } from 'react'
import { ExtendedEvent } from '../types'
import { handleKeyboardNavigation, getEventAccessibleDescription } from '../utils/accessibility'

interface AccessibleEventProps {
  event: ExtendedEvent
  children: React.ReactNode
  onClick?: () => void
  onKeyboardNavigation?: (direction: 'up' | 'down' | 'left' | 'right') => void
  tabIndex?: number
}

/**
 * Accessible event wrapper component
 * Adds proper ARIA attributes and keyboard navigation to event components
 */
export function AccessibleEvent({
  event,
  children,
  onClick,
  onKeyboardNavigation,
  tabIndex = 0,
}: AccessibleEventProps) {
  const eventRef = useRef<HTMLDivElement>(null)

  // Generate accessible description for screen readers
  const accessibleDescription = getEventAccessibleDescription(event)

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      handleKeyboardNavigation(e, () => onClick && onClick(), onKeyboardNavigation)
    },
    [onClick, onKeyboardNavigation]
  )

  return (
    <div
      ref={eventRef}
      className="accessible-event"
      role="button"
      tabIndex={tabIndex}
      aria-label={event.title}
      aria-description={accessibleDescription}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-event-id={event.id}
    >
      {children}
    </div>
  )
}

export default AccessibleEvent
